import { Zap, Briefcase, Gift, HardDrive, Monitor } from 'react-feather'
import { ITimeline } from 'types/Timeline'
import Image from 'components/Image'
import CustomLink from 'components/Link'
import LanguageBadge from 'components/LanguageBadge'
import siteMetadata from './siteMetadata'

const Link = ({ children, ...rest }) => {
  return (
    <CustomLink className="text-primary-600 dark:text-primary-500" {...rest}>
      {children}
    </CustomLink>
  )
}

const resumeData: ITimeline[] = [
  {
    title: 'Joined Postman as Software Engineer 🧑‍🚀',
    subtitle: 'June 2022 - Present',
    image: (
      <Image
        src="/static/images/resume/logos/postman.png"
        width={24}
        height={24}
        alt="Postman logo"
      />
    ),
    content: (
      <>
        <p>
          To keep my career path aligned with the goals I have prepared for myself, I decided to
          join <Link href="https://www.postman.com/">Postman</Link> as Software Engineer.
        </p>
        <p>
          I have a particular interest in dev tooling and plan to work on a few side projects around
          dev tooling myself, so Postman was the perfect choice to learn and understand about dev
          tools.
        </p>
        <p>
          Also the scale at which Postman is operating is{' '}
          <Link href="https://blog.postman.com/celebrating-20-million-postman-users/">huge</Link>{' '}
          and I am hoping to contribute to the upcoming successes of the company soon.
        </p>
        <p>
          I am joining the{' '}
          <Link href="https://www.postman.com/product/workspaces/">Workspaces</Link> team at Postman
          and hoping to contribute to the success of the project and grow both myself and the
          company. 🧑‍🚀
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="javascript" />
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="mysql" />
        <LanguageBadge language="aws" />
        <LanguageBadge language="electron" />
      </>
    ),
    learnMore: `${siteMetadata.pages.newsletter}archive/1199958`,
  },
  {
    title: 'First project which reached 300K hits 🤯',
    subtitle: 'Released on 1st May, 2022',
    learnMore: '/blog/journey-into-how-to-professionally-say-project/?ref=resume-learn-more',
    image: <Zap size={18} />,
    content: (
      <>
        <p>
          I follow a content creator on Instagram called{' '}
          <Link href="https://www.instagram.com/loewhaley/">@loewhaley</Link> and I like her content
          regarding some phrases that you might really feel like saying but what you can say instead
          to make it a bit more "professional".
        </p>
        <p>
          Of course, most of them are satirical but some are actually very good. I found myself
          noting down a few of these in a markdown file. So instead just created a simple static
          site with this data and added features of making it easier to search and give a way for
          anyone wanting to print them or save them as PDF.
        </p>
        <p>
          With this, the project "How to professionally say" was born. This project got a lot of
          traction from the community at Hackernews and Reddit and the site blew up.
        </p>
        <p>
          This is an amazing experience and I have written a detailed blog about the technical side
          of the project which you can read more by checking the link below.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="nextjs" />
        <LanguageBadge language="tailwind" />
        <LanguageBadge language="cf-pages" />
      </>
    ),
  },
  {
    title: 'Started working at Cimpress ❄️',
    subtitle: 'April 2021 - June 2022',
    image: (
      <Image
        src="/static/images/resume/logos/cimpress.png"
        width={24}
        height={24}
        alt="Cimpress logo"
      />
    ),
    content: (
      <>
        <p>
          During my college days, I had a few seniors who started working at{' '}
          <Link href="https://cimpress.com">Cimpress</Link>. I had heard nothing but good things
          about the work and culture of Cimpress so naturally, I wanted to explore an opportunity at
          Cimpress.
        </p>
        <p>
          Unfortunately, I could not get the chance to work at Cimpress as soon as I graduated but
          later down the line I did receive an opportunity at Cimpress and wanted to try it out so I
          took it.
        </p>
        <p>
          I got to work with amazing colleagues and interacted with lots of great engineers working
          in my domain. I worked on services that helped Cimpress's{' '}
          <Link href="https://mcp.cimpress.io">MCP</Link> (Mass Customization Platform) with the
          fulfillers and vice versa connecting notifications from fulfillers to the MCP.
        </p>
        <p>
          I got the chance to work on both independent backend services and UI components as well as
          work on improving features of existing services.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="typescript" />
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="aws" />
        <LanguageBadge language="react" />
      </>
    ),
  },
  {
    title: 'First corporate job starting at PeopleGrove 🤩',
    subtitle: 'August 2020 - April 2021',
    image: (
      <Image
        src="/static/images/resume/logos/peoplegrove.png"
        width={24}
        height={24}
        alt="PeopleGrove logo"
      />
    ),
    content: (
      <>
        <p>
          Situations were getting bad, the companies I had offers from were uncertain about how they
          would be handling the situation with new hires, rumors were going around about companies
          rescinding offers (and many companies did).
        </p>
        <p>
          With no clear response from the company I was planning to join, I had no other option but
          to get back up and start the job hunt once again.
        </p>
        <p>
          I felt that startups were a nice place for me personally because I always loved working at
          startups even during my internship periods, there were quite a few who were hiring
          software engineers and yet alone freshers.
        </p>
        <p>
          There I came across a job posting by{' '}
          <Link href="https://peoplegrove.com">PeopleGrove</Link> which was asking for 1+ year
          experience candidate but I still went ahead and applied for it.
        </p>
        <p>
          Luckily I got a call and was scheduled for the 5 rounds of interview, I was grateful that
          they extended me an offer (even though I was technically not yet graduated since my exams
          were delayed). I joined PeopleGrove as an SDE-1 🎉
        </p>
        <p>
          Being a small startup, their product was humongous. Since it was a small team I had quite
          difficulties initially understanding the modules and the projects that were governed by
          the team I was joining. Naturally, I was able to contribute more to the projects.
        </p>
        <p>
          I was able to complete two major projects and worked on a supplemental release of an
          existing project. It was a great experience and the people there were awesome both
          technically and personally.
        </p>
        <p>
          A funny story, on my last farewell call, I was called by almost everyone "the guy who asks
          a lot" 😅. Being my first job I had tons of questions both about the technical projects
          and in general about the company. Thank you{' '}
          <Link href="https://in.linkedin.com/in/nikeshhayaran">Nikesh</Link> for answering all of
          my questions regarding taxations so patiently 🙂 and everyone else with whom I had a
          chance to talk and work.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="javascript" />
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="postgres" />
        <LanguageBadge language="react" />
      </>
    ),
  },
  {
    title: 'Internship at Frontier Techworks 🤓',
    subtitle: 'June 2020 - August 2020',
    image: (
      <Image
        src="/static/images/resume/logos/ftworks.png"
        width={20}
        height={20}
        alt="Frontier Techworks logo"
      />
    ),
    content: (
      <>
        <p>
          So it was the starting era of the{' '}
          <Link href="https://covid19.india.gov.in/">Covid19</Link> virus, colleges were shut and
          there was no information about my final B.E exams. Naturally, everything had stopped and
          there was no certainty about what is going to happen.
        </p>
        <p>
          Given these situations, I knew I should not waste my time and instead try to find some
          opportunities till things go back to normal (as I hoped they would in a couple of months
          😑). I started looking for some startup that is hiring interns or contractors and I came
          across{' '}
          <Link href="https://www.linkedin.com/company/frontier-techworks/">
            Frontier Techworks
          </Link>
          . (Sad to see their site is down 😧)
        </p>
        <p>
          I worked as a Full-Stack Developer, majorly focusing on the backend and worked on their
          new project called FreshVet.
        </p>
        <p>
          I worked on core modules which included <b>Mobile OTP based Authentication</b>,{' '}
          <b>Real time chat</b> using websocket subscriptions, <b>Appointment Booking System</b>,{' '}
          <b>Smart Scheduling for Vets</b>, Firebase <b>Push Notifications</b> etc.
        </p>
        <p>
          At the ending phase by extending my duration I also worked on in integrating the APIs on
          the web client with Gatsby using Apollo client.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="typescript" />
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="graphql" />
        <LanguageBadge language="mongo" />
        <LanguageBadge language="gatsby" />
      </>
    ),
  },
  {
    title: 'Contract work at Serrala 👨‍💻',
    subtitle: 'February 2020 - June 2020',
    image: (
      <Image
        src="/static/images/resume/logos/serrala.png"
        width={20}
        height={20}
        alt="Serrala logo"
      />
    ),
    content: (
      <>
        <p>
          With a help of a friend, I also started working on a contract basis with{' '}
          <Link href="https://serrala.com">Serrala</Link>.
        </p>
        <p>
          I worked as an Angular developer on their project of one-to-one migration of the web
          portal of Serrala Alevate. My day-to-day task used to be fixing and improving the legacy
          codebase and adding new features as planned by their roadmap.
        </p>
        <p>
          This was a rather different experience compared to working as a freelancer and intern but
          I loved it.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="angular" />
      </>
    ),
  },
  {
    title: 'First Internship at MagTapp 🎉',
    subtitle: 'February 2020 - June 2020',
    image: (
      <Image
        src="/static/images/resume/logos/magtapp.png"
        width={20}
        height={20}
        alt="MagTapp logo"
      />
    ),
    content: (
      <>
        <p>
          Working as a freelancer gave me confidence and experience in working as a solo developer,
          I wanted to experience a bit about how a developer works in a corporate environment.
        </p>
        <p>
          Although I always loved working in teams in many of these competitions, working at a
          company is a different experience, hence I started applying for some Internship
          opportunities
        </p>
        <p>
          I wanted to experience more on the dev side of the things so I choose to mostly do
          internships in early-age startups where I can learn more about the engineering side of the
          things and I was fortunate to be able to get my first opportunity in one such startup.
        </p>
        <p>
          I worked at <Link href="https://magtapp.com">MagTapp</Link> as Backend Engineer, I
          remember when I joined they didn't even have any Backend engineers specifically, just a
          few people for Android app development, So this was a great opportunity for me to learn
          and explore.
        </p>
        <p>
          This went well, well enough that they decided to increase my stipend by 50% due to my work
          performance and ethics.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="javascript" />
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="mongo" />
        <LanguageBadge language="aws" />
      </>
    ),
  },
  {
    title: 'Started working as Freelancer 🈺',
    subtitle: 'Started around mid 2019',
    image: <Briefcase size={18} />,
    content: (
      <>
        <p>
          Something was odd with how my college life was going on. On one side I was enjoying the
          part of participating and building solutions during all these competitions and Hackathons,
          in some we won and in few we lost but one thing was very common in all of these
          competitions...
        </p>
        <p>
          Once the competition was over, the project would get archived and thrown into a
          `/dev/projects` folder on my system and never saw the light of a day. I never experienced
          how these projects would survive if thrown in front of users and felt this is something
          that is lacking for me. Hence I started working as a freelancer.
        </p>
        <p>
          This was a turning point where the projects I was building were not closer to the
          technical solutions I was developing in the competitions but instead, I was able to put
          the projects in hands of users and test things out, iterate over the feedback and build
          amazing user-friendly features.
        </p>
        <p>
          I started this as a fun and learning activity but it turned out to be one of my best
          decisions, so I continued this from mid-3rd year to my final year.
        </p>
        <p>
          In total I had build around <b>6 projects</b> for different clients (
          <i>both national and international</i>)
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="javascript" />
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="mongo" />
        <LanguageBadge language="aws" />
      </>
    ),
  },
  {
    title: 'First Prize Winner in HackStomp 🥇',
    subtitle: 'February 2019',
    image: <Gift size={18} />,
    content: (
      <>
        <p>
          In a few months, there was another Hackathon hosted by{' '}
          <Link href="https://universalcollegeofengineering.edu.in/">
            Universal College of Engineering
          </Link>
          .
        </p>
        <p>
          I and my team decided to participate in this and we won first prize this time 🏆. I
          remember we were exhausted to even celebrate but it was an awesome feeling indeed.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="mongo" />
        <LanguageBadge language="android" />
        <LanguageBadge language="react" />
      </>
    ),
    learnMore:
      'https://universalcollegeofengineering.edu.in/wp-content/uploads/2019/07/Report-HACKSTOMP.pdf',
  },
  {
    title: 'Second Prize Winner in VCET Hackathon 🥈',
    subtitle: 'October 2018',
    image: <HardDrive size={18} />,
    content: (
      <>
        <p>
          Here we were, participating again in the Hackathon at VCET for next year. But the results
          were different this time, not just we got selected for the top 5 finalists but we also won
          second prize 🥈.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="nodejs" />
        <LanguageBadge language="mongo" />
        <LanguageBadge language="android" />
      </>
    ),
  },
  {
    title: 'Participated in first Hackathon 🎉',
    subtitle: 'October 2017',
    image: <Monitor size={18} />,
    content: (
      <>
        <p>
          During my second year, I participated in a team of three in the first Hackathon which was
          hosted by VCET. Starting with learning Java and MySQL, we build a desktop application
          using Java Swings connected to MySQL database called <b>SafeStepz</b>
        </p>
        <p>
          It was a portal made for government employees to keep track of the number of users
          available on any certain bridge Railway station.
        </p>
        <p>
          We were the only team from the second year that was shortlisted for the top 5 teams and
          got the chance to present the project in front of the panel of judges. That was an amazing
          experience that motivated me to continue this journey toward software development.
        </p>
      </>
    ),
    tags: (
      <>
        <LanguageBadge language="java" />
        <LanguageBadge language="mysql" />
      </>
    ),
  },
  {
    title: 'Completed Bachelor of Engineering 🎓',
    subtitle: 'June 2016 - June 2020',
    image: (
      <Image src="/static/images/resume/logos/vcet.png" width={20} height={20} alt="VCET logo" />
    ),
    content: (
      <>
        <p>
          Well let's start from here, So I completed my Bachelor of Engineering in Information
          Technology from <Link href="https://vcet.edu.in">V.C.E.T</Link>. One primary reason to
          select the college was that it was closer to where I stay and also that I was not very
          adventurous to have a daily commute{' '}
          <Link href="https://www.youtube.com/watch?v=_CSU-_6F-bY">like this</Link>
        </p>
        <p>
          I was able to find my interest in web development when I started participating in
          Hackathons which were helpful to make an early decision on what all things I wanted to
          learn which included HTML, CSS and JavaScript. Later I started with Backend technologies
          with Node.js and MongoDB
        </p>
        <p>
          Also I, along with my friends founded the <i>Core Coding Committee</i> where we focused on
          peer learning. I became the Technical Head of this committee and it was a nice experience.
        </p>
      </>
    ),
  },
]

export default resumeData
